import { Comment, DemoVideo, ItemAuditDemoFile, User, VUFile } from "@/models"
import { fetchDemoVideoVersions } from "@/store/modules/demoVideo.module"

export type DemoVideoVersion = {
  id: string
  title: string
  size: string
  dateRange: string
  uploadedAt: string
  previewUrl: string
  current: boolean
  owner: User | null
  demoVideo: DemoVideo
}

export type DemoVideoCommentDraft = {
  message: string
  files: VUFile[]
}

export enum DemoVideoVersionState {
  CURRENT = "CURRENT",
  NOT_CURRENT = "NOT_CURRENT",
  DELETED = "DELETED"
}

export type DemoVideoVersionInfo = {
  version: number
  state: DemoVideoVersionState
}

export function formatDemoVideoVersionTitle(number?: number): string {
  return number ? `Demo Video - V.${number}` : "Demo Video"
}

/**
 * Assigns each demo video a permanent, never-reused version number and its
 * current/previous/deleted state. The backend now returns the full history
 * (including deleted videos) sorted by createdDate DESC, so this is a plain
 * index over the already-authoritative list — no more guessing about deleted
 * videos the client never learned about.
 */
export function buildDemoVideoVersionMap(
  demoVideos: DemoVideo[]
): Record<string, DemoVideoVersionInfo> {
  const chronological = [...demoVideos].reverse()

  return Object.fromEntries(
    chronological.map((demoVideo, index) => [
      demoVideo.id,
      {
        version: index + 1,
        state: demoVideo.current
          ? DemoVideoVersionState.CURRENT
          : demoVideo.deleted
            ? DemoVideoVersionState.DELETED
            : DemoVideoVersionState.NOT_CURRENT
      } satisfies DemoVideoVersionInfo
    ])
  )
}

export function getDemoVideoVersionLabel(
  demoVideoId: string,
  versionMap: Record<string, DemoVideoVersionInfo>
): string {
  return formatDemoVideoVersionTitle(versionMap[demoVideoId]?.version)
}

export function isDemoVideoIdDeleted(
  demoVideoId: string,
  versionMap: Record<string, DemoVideoVersionInfo>
): boolean {
  return !!demoVideoId && versionMap[demoVideoId]?.state === DemoVideoVersionState.DELETED
}

export function isPreviousDemoVideoId(
  demoVideoId: string,
  versionMap: Record<string, DemoVideoVersionInfo>
): boolean {
  return !!demoVideoId && versionMap[demoVideoId]?.state === DemoVideoVersionState.NOT_CURRENT
}

export function useDemoVideoVersions() {
  async function loadDemoVideoVersions(payload: {
    projectId?: string
    targetId: string
  }): Promise<DemoVideo[]> {
    if (!payload.targetId) return []

    return fetchDemoVideoVersions({
      projectId: payload.projectId,
      resourceType: "ITEM",
      targetId: payload.targetId
    })
  }

  return { loadDemoVideoVersions }
}

export type DemoVideoModalParams = {
  initialTimestampSeconds?: number
  scrollToCommentId?: string
  demoVideoId?: string
  demoFile?: ItemAuditDemoFile | null
}

type DemoVideoTimestampSelectPayload = {
  seconds?: number
  commentId: string
  comment?: Partial<Pick<Comment, "demoVideoId" | "itemAuditRecord">>
}

export function resolveDemoVideoVersionId(
  params: DemoVideoModalParams | undefined,
  demoVideos: DemoVideo[]
): string {
  if (params?.demoVideoId) return params.demoVideoId

  const fileMetadataId = params?.demoFile?.demoFileMetadata?.id
  const videoUrl = params?.demoFile?.demoVideoUrl?.trim()
  if (!fileMetadataId && !videoUrl) return ""

  return (
    demoVideos
      .filter(demoVideo =>
        fileMetadataId
          ? demoVideo.fileMetadataId === fileMetadataId
          : demoVideo.videoUrl.trim() === videoUrl
      )
      .sort((a, b) => Date.parse(a.createdDate) - Date.parse(b.createdDate))[0]?.id ?? ""
  )
}

export function buildDemoVideoModalParams(
  payload?: number | DemoVideoModalParams | DemoVideoTimestampSelectPayload
): DemoVideoModalParams | undefined {
  if (typeof payload === "number") return { initialTimestampSeconds: payload }
  if (!payload) return undefined

  if ("commentId" in payload) {
    return {
      initialTimestampSeconds: payload.seconds,
      scrollToCommentId: payload.commentId,
      demoVideoId: payload.comment?.demoVideoId,
      demoFile: payload.comment?.itemAuditRecord?.after?.demoFile
    }
  }

  return payload
}
